import React, {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { calculatePosition } from "../../../../utils/calculate-position";
import { parsePathToVertices } from "../../../../utils/svg-path-to-vertices";
import Matter, {
  Bodies,
  Common,
  Engine,
  Events,
  Mouse,
  MouseConstraint,
  Query,
  Render,
  Runner,
  World,
} from "matter-js";

const GravityContext = createContext(null);

export const MatterBody = ({
  children,
  className,
  matterBodyOptions = { friction: 0.1, restitution: 0.1, density: 0.001, isStatic: false },
  isDraggable = true,
  bodyType = "rectangle",
  sampleLength = 15,
  x = 0,
  y = 0,
  angle = 0,
  ...props
}) => {
  const elementRef = useRef(null);
  const idRef = useRef(Math.random().toString(36).substring(7));
  const context = useContext(GravityContext);

  useEffect(() => {
    if (!elementRef.current || !context) return;
    const id = idRef.current;
    context.registerElement(id, elementRef.current, {
      children,
      matterBodyOptions,
      bodyType,
      sampleLength,
      isDraggable,
      x,
      y,
      angle,
      ...props,
    });
    return () => context.unregisterElement(id);
  }, [children, matterBodyOptions, isDraggable, bodyType, sampleLength, x, y, angle, props, context]);

  return (
    <div ref={elementRef} className={["absolute", className, isDraggable ? "pointer-events-none" : ""].filter(Boolean).join(" ")}>{children}</div>
  );
};

const Gravity = forwardRef(function Gravity(
  {
    children,
    debug = false,
    gravity = { x: 0, y: 1 },
    grabCursor = true,
    resetOnResize = true,
    addTopWall = true,
    autoStart = true,
    className,
    ...props
  },
  ref
) {
  const canvas = useRef(null);
  const engine = useRef(Engine.create());
  const render = useRef();
  const runner = useRef();
  const bodiesMap = useRef(new Map());
  const frameId = useRef();
  const mouseConstraint = useRef();
  const mouseDown = useRef(false);
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });

  const debounce = (fn, wait = 300) => { let t; return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), wait); }; };

  const registerElement = useCallback((id, element, elProps) => {
    if (!canvas.current) return;
    const width = element.offsetWidth;
    const height = element.offsetHeight;
    const rect = canvas.current.getBoundingClientRect();
    const angleRad = (elProps.angle || 0) * (Math.PI / 180);
    const posX = calculatePosition(elProps.x, rect.width, width);
    const posY = calculatePosition(elProps.y, rect.height, height);

    let body;
    if (elProps.bodyType === "circle") {
      const r = Math.max(width, height) / 2;
      body = Bodies.circle(posX, posY, r, {
        ...elProps.matterBodyOptions,
        angle: angleRad,
        render: { fillStyle: debug ? "#888888" : "#00000000", strokeStyle: debug ? "#333333" : "#00000000", lineWidth: debug ? 3 : 0 },
      });
    } else if (elProps.bodyType === "svg") {
      const paths = element.querySelectorAll("path");
      const vertexSets = [];
      paths.forEach((p) => { const d = p.getAttribute("d"); if (d) vertexSets.push(parsePathToVertices(d, elProps.sampleLength)); });
      body = Bodies.fromVertices(posX, posY, vertexSets, {
        ...elProps.matterBodyOptions,
        angle: angleRad,
        render: { fillStyle: debug ? "#888888" : "#00000000", strokeStyle: debug ? "#333333" : "#00000000", lineWidth: debug ? 3 : 0 },
      });
    } else {
      body = Bodies.rectangle(posX, posY, width, height, {
        ...elProps.matterBodyOptions,
        angle: angleRad,
        render: { fillStyle: debug ? "#888888" : "#00000000", strokeStyle: debug ? "#333333" : "#00000000", lineWidth: debug ? 3 : 0 },
      });
    }

    if (body) {
      World.add(engine.current.world, [body]);
      bodiesMap.current.set(id, { element, body, props: elProps });
    }
  }, [debug]);

  const unregisterElement = useCallback((id) => {
    const item = bodiesMap.current.get(id);
    if (item) {
      World.remove(engine.current.world, item.body);
      bodiesMap.current.delete(id);
    }
  }, []);

  const updateElements = useCallback(() => {
    bodiesMap.current.forEach(({ element, body }) => {
      const { x, y } = body.position;
      const rotation = body.angle * (180 / Math.PI);
      element.style.transform = `translate(${x - element.offsetWidth / 2}px, ${y - element.offsetHeight / 2}px) rotate(${rotation}deg)`;
    });
    frameId.current = requestAnimationFrame(updateElements);
  }, []);

  const startEngine = useCallback(() => {
    if (runner.current) Runner.run(runner.current, engine.current);
    if (render.current) Render.run(render.current);
    frameId.current = requestAnimationFrame(updateElements);
  }, [updateElements]);

  const initializeRenderer = useCallback(() => {
    if (!canvas.current) return;
    const height = canvas.current.offsetHeight;
    const width = canvas.current.offsetWidth;
    // eslint-disable-next-line global-require
    Common.setDecomp(require("poly-decomp"));
    engine.current.gravity.x = gravity.x;
    engine.current.gravity.y = gravity.y;
    render.current = Render.create({ element: canvas.current, engine: engine.current, options: { width, height, wireframes: false, background: "#00000000" } });
    const mouse = Mouse.create(render.current.canvas);
    mouseConstraint.current = MouseConstraint.create(engine.current, { mouse, constraint: { stiffness: 0.2, render: { visible: debug } } });
    const walls = [
      Bodies.rectangle(width / 2, height + 10, width, 20, { isStatic: true, friction: 1, render: { visible: debug } }),
      Bodies.rectangle(width + 10, height / 2, 20, height, { isStatic: true, friction: 1, render: { visible: debug } }),
      Bodies.rectangle(-10, height / 2, 20, height, { isStatic: true, friction: 1, render: { visible: debug } }),
    ];
    if (addTopWall) walls.push(Bodies.rectangle(width / 2, -10, width, 20, { isStatic: true, friction: 1, render: { visible: debug } }));
    const touchingMouse = () => Query.point(engine.current.world.bodies, mouseConstraint.current?.mouse.position || { x: 0, y: 0 }).length > 0;
    if (grabCursor) {
      Events.on(engine.current, "beforeUpdate", () => {
        if (!canvas.current) return;
        if (!mouseDown.current && !touchingMouse()) canvas.current.style.cursor = "default";
        else if (touchingMouse()) canvas.current.style.cursor = mouseDown.current ? "grabbing" : "grab";
      });
      canvas.current.addEventListener("mousedown", () => { mouseDown.current = true; if (!canvas.current) return; canvas.current.style.cursor = touchingMouse() ? "grabbing" : "default"; });
      canvas.current.addEventListener("mouseup", () => { mouseDown.current = false; if (!canvas.current) return; canvas.current.style.cursor = touchingMouse() ? "grab" : "default"; });
    }
    World.add(engine.current.world, [mouseConstraint.current, ...walls]);
    render.current.mouse = mouse;
    runner.current = Runner.create();
    Render.run(render.current);
    updateElements();
    if (autoStart) startEngine();
  }, [updateElements, addTopWall, grabCursor, gravity, debug, autoStart, startEngine]);

  const clearRenderer = useCallback(() => {
    if (frameId.current) cancelAnimationFrame(frameId.current);
    if (mouseConstraint.current) World.remove(engine.current.world, mouseConstraint.current);
    if (render.current) {
      Matter.Mouse.clearSourceEvents(render.current.mouse);
      Render.stop(render.current);
      render.current.canvas.remove();
    }
    if (runner.current) Runner.stop(runner.current);
    if (engine.current) { World.clear(engine.current.world, false); Engine.clear(engine.current); }
    bodiesMap.current.clear();
  }, []);

  const handleResize = useCallback(() => {
    if (!canvas.current || !resetOnResize) return;
    const newWidth = canvas.current.offsetWidth;
    const newHeight = canvas.current.offsetHeight;
    setCanvasSize({ width: newWidth, height: newHeight });
    clearRenderer();
    initializeRenderer();
  }, [clearRenderer, initializeRenderer, resetOnResize]);

  const stopEngine = useCallback(() => {
    if (runner.current) Runner.stop(runner.current);
    if (render.current) Render.stop(render.current);
    if (frameId.current) cancelAnimationFrame(frameId.current);
  }, []);

  const reset = useCallback(() => {
    stopEngine();
    bodiesMap.current.forEach(({ element, body, props }) => {
      body.angle = props.angle || 0;
      const x = calculatePosition(props.x, canvasSize.width, element.offsetWidth);
      const y = calculatePosition(props.y, canvasSize.height, element.offsetHeight);
      body.position.x = x; body.position.y = y;
    });
    updateElements();
    handleResize();
  }, [canvasSize.width, canvasSize.height, handleResize, stopEngine, updateElements]);

  useImperativeHandle(ref, () => ({ start: startEngine, stop: stopEngine, reset }), [startEngine, stopEngine, reset]);

  useEffect(() => {
    const debouncedResize = debounce(handleResize, 500);
    window.addEventListener("resize", debouncedResize);
    return () => window.removeEventListener("resize", debouncedResize);
  }, [handleResize]);

  useEffect(() => { initializeRenderer(); return clearRenderer; }, [initializeRenderer, clearRenderer]);

  return (
    <GravityContext.Provider value={{ registerElement, unregisterElement }}>
      <div ref={canvas} className={["absolute top-0 left-0 w-full h-full", className].filter(Boolean).join(" ")} {...props}>
        {children}
      </div>
    </GravityContext.Provider>
  );
});

export default Gravity;


